/**
 * React Query hook for Batch Billing Aggregate
 * Fetches summary/aggregate data for selected cases
 */

import { useQuery } from '@tanstack/react-query';
import { getBatchBillingAggregate } from 'services/billingServices';
import { queryKeys } from '../../../config/queryClient';

/**
 * Fetches batch billing aggregate data for selected cases
 *
 * @param {Array} case_list - Array of case UUIDs
 * @returns {Promise<Object>} - Aggregate summary data
 */
const fetchBatchBillingAggregate = async (case_list = []) => {
  try {
    if (!case_list || case_list.length === 0) {
      return {
        uninvoiced_billable_amount: 0,
        unbilled_expenses_amount: 0,
        total_invoice_due_amount: 0,
        total_unbilled_amount: 0,
        case_count: 0,
      };
    }

    const response = await getBatchBillingAggregate(case_list);

    // API returns data nested in { data: { actualData } }
    if (response?.data?.data) {
      return response.data.data;
    }

    // Fallback if data is at response.data directly
    if (response?.data) {
      return response.data;
    }

    return {
      uninvoiced_billable_amount: 0,
      unbilled_expenses_amount: 0,
      total_invoice_due_amount: 0,
      total_unbilled_amount: 0,
      case_count: 0,
    };
  } catch (error) {
    console.error('Error fetching batch billing aggregate:', error);
    throw error;
  }
};

/**
 * Hook to fetch batch billing aggregate using React Query
 *
 * @param {Object} options - Configuration options
 * @param {Array} options.case_list - Array of case UUIDs to get aggregate for
 * @param {boolean} options.enabled - Enable/disable query (default: true)
 *
 * @returns {Object} Query result object
 * @returns {Object} returns.aggregateData - Aggregate summary data
 * @returns {boolean} returns.isLoading - Initial loading state
 * @returns {boolean} returns.isFetching - Fetching state
 * @returns {boolean} returns.isError - Error state indicator
 * @returns {Object|null} returns.error - Error object if request failed
 * @returns {Function} returns.refetch - Function to manually refetch data
 *
 * @example
 * const {
 *   aggregateData,
 *   isLoading,
 *   isError
 * } = useBatchBillingAggregateQuery({
 *   case_list: ['case-uuid-1', 'case-uuid-2'],
 *   enabled: true
 * });
 */
export const useBatchBillingAggregateQuery = ({ case_list = [], enabled = true } = {}) => {
  const queryKey = ['batch-billing-aggregate', { case_list }];

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey,
    queryFn: () => fetchBatchBillingAggregate(case_list),
    enabled: enabled && case_list.length > 0,
    staleTime: 2 * 60 * 1000, // Consider data stale after 2 minutes
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: false,
    retry: (failureCount) => {
      return failureCount < 2;
    },
  });

  return {
    aggregateData: data || {
      uninvoiced_billable_amount: 0,
      unbilled_expenses_amount: 0,
      total_invoice_due_amount: 0,
      total_unbilled_amount: 0,
      case_count: 0,
    },
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  };
};

export default useBatchBillingAggregateQuery;
